import { ComponentRef, Injectable, Injector } from '@angular/core';
import { Overlay, OverlayRef, ConnectedPosition, FlexibleConnectedPositionStrategy } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { HelpdeskCardComponent } from '../component/helpdesk-card/helpdesk-card.component';

/**
 * Monta/desmonta o card de helpdesk via CDK Overlay (mesmo stack do
 * `mat-menu`): `FlexibleConnectedPositionStrategy` ancora ao botão de
 * origem, scroll/resize reflowam, backdrop transparente captura o click
 * "fora", ESC fecha. Substitui o fluxo jQuery legado do antigo
 * `app-support-button`.
 *
 * Singleton global (`providedIn: 'root'`) — o estado de aberto/fechado
 * é compartilhado entre os múltiplos botões de helpdesk (navbar do
 * autor/revisor + chair area moderno + chair area clássico).
 */
@Injectable({ providedIn: 'root' })
export class HelpdeskOverlayService {
  private overlayRef: OverlayRef | null = null;
  private componentRef: ComponentRef<HelpdeskCardComponent> | null = null;
  private subs: Subscription[] = [];

  private readonly _isOpen$ = new BehaviorSubject<boolean>(false);
  readonly isOpen$: Observable<boolean> = this._isOpen$.asObservable();

  constructor(
    private overlay: Overlay,
    private injector: Injector,
  ) {}

  isOpen(): boolean { return this._isOpen$.value; }

  /** Toggle — abre se fechado, fecha se aberto. */
  toggle(origin: HTMLElement | null | undefined): void {
    if (this.isOpen()) this.close();
    else this.open(origin);
  }

  open(origin: HTMLElement | null | undefined): void {
    if (!origin) {
      console.warn('[HelpdeskOverlay] open() chamado sem origin');
      return;
    }
    if (this.overlayRef) return;

    try {
      const positionStrategy = this.buildPositionStrategy(origin);

      this.overlayRef = this.overlay.create({
        positionStrategy,
        hasBackdrop: true,
        backdropClass: 'cdk-overlay-transparent-backdrop',
        scrollStrategy: this.overlay.scrollStrategies.reposition(),
        panelClass: 'helpdesk-overlay-panel',
      });

      const portal = new ComponentPortal(HelpdeskCardComponent);
      this.componentRef = this.overlayRef.attach(portal);

      this.subs.push(
        this.overlayRef.backdropClick().subscribe(() => this.close()),
        this.overlayRef.keydownEvents().subscribe(ev => {
          if (ev.key === 'Escape') {
            ev.preventDefault();
            this.close();
          }
        }),
        this.componentRef.instance.requestClose.subscribe(() => this.close()),
      );

      this._isOpen$.next(true);
    } catch (err) {
      console.error('[HelpdeskOverlay] falha ao abrir', err);
      if (this.overlayRef) {
        this.overlayRef.dispose();
        this.overlayRef = null;
      }
    }
  }

  close(): void {
    if (!this.overlayRef) return;
    this.subs.forEach(s => s.unsubscribe());
    this.subs = [];
    this.overlayRef.detach();
    this.overlayRef.dispose();
    this.overlayRef = null;
    this.componentRef = null;
    this._isOpen$.next(false);
  }

  /**
   * Posições preferenciais: encostado ao botão (gap 0, igual `mat-menu`
   * `[overlapTrigger]="false"`). Quatro variantes — acima/esquerda,
   * abaixo/esquerda, acima/direita, abaixo/direita — CDK escolhe a
   * primeira que cabe na viewport. `withPush(true)` empurra pra dentro
   * se ainda assim transbordar nas bordas.
   */
  private buildPositionStrategy(origin: HTMLElement): FlexibleConnectedPositionStrategy {
    const positions: ConnectedPosition[] = [
      // Acima do botão, alinhado pela esquerda (caso chair area: botão no canto inferior-esquerdo).
      { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom' },
      // Abaixo do botão, alinhado pela esquerda.
      { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top' },
      // Abaixo do botão, alinhado pela direita (caso navbar autor/revisor: botão no canto superior-direito).
      { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top' },
      // Acima do botão, alinhado pela direita.
      { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom' },
    ];
    return this.overlay
      .position()
      .flexibleConnectedTo(origin)
      .withPositions(positions)
      .withPush(true)
      .withFlexibleDimensions(false)
      .withViewportMargin(8);
  }
}
